import React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Center, Input } from 'legacyComponents/styled/lib';
import { conf } from 'apps/socialAccounts/socialAccounts.yaml';
import { HoverElement as HoverElementComponent } from 'legacyComponents/layout/hoverElement';

import PropTypes from 'prop-types';
import { withNamespaces, Trans } from 'react-i18next';
import { media } from 'legacyComponents/styled/styleUtils';

import { Icon } from 'components';
import { withConditional } from 'legacyComponents/hoc/conditional';

const HoverElement = withConditional(HoverElementComponent);

export class SocialAccountEditor {
  constructor(app, account, accountType) {
    this.app = app;
    this.account = account;
    this.accountType = accountType;
    if (!account) {
      this.accountName = '';
    } else {
      this.accountName = account.accountName;
    }
  }

  @observable accountType;
  @observable accountName;
  @action UpdateValue = newVal => {
    if (
      this.accountName === '' &&
      newVal.length <= conf[this.accountType].prefix.length
    ) {
      return;
    }
    this.accountName = newVal.slice(conf[this.accountType].prefix.length);
    if (this.account) {
      this.account.accountName = this.accountName;
      this.account.deleted = false; // in case it was deleted in the meantime
    }
  };

  @action Complete() {
    if (this.accountName.length === 0 && this.account) {
      this.app.Delete(this.account);
    } else if (!this.account) {
      this.app.Add(this.accountType, this.accountName);
    }
  }
}

@withNamespaces()
@observer
export class SocialStats extends React.Component {
  @observable element = null;
  componentDidUpdate() {
    if (this._input) {
      this._input.focus();
    }
  }

  onEnter = e => {
    if (e.which === 13) {
      this.props.app.socialAccounts.accountEditor.Complete();
      this.props.app.socialAccounts.StopEditAccount();
    }
  };

  onBlur = () => {
    this.props.app.socialAccounts.accountEditor.Complete();
    this.props.app.socialAccounts.StopEditAccount();
  };

  onClickAccount = (account, accType, e) => {
    this.element = e.target;
    this.props.app.socialAccounts.StartEditAccount(account, accType);
  };

  render() {
    const { app } = this.props;
    const { socialAccounts = [], editing } = app;
    const {
      content: { followersCount },
      followingCount,
    } = app;

    const isProfile = app.constructor.registryName === 'ProfileApp';
    const accountTypeMap = {};
    const accountsNumber = socialAccounts.filter(account => !account.deleted)
      .length;
    const accountEditor = socialAccounts && socialAccounts.accountEditor;

    socialAccounts.forEach(account => {
      if (!account.deleted) {
        accountTypeMap[account.accountType] = account;
      }
    });

    const Wrapper = ['CollectionApp', 'ProfileApp'].includes(
      app.constructor.registryName,
    )
      ? Center
      : React.Fragment;

    return (
      <Wrapper>
        <List condition={editing} isProfile={isProfile}>
          {Object.keys(conf).map(accountType => (
            <Item
              key={accountType}
              id={accountType}
              onClick={e => {
                this.onClickAccount(
                  accountTypeMap[accountType],
                  accountType,
                  e,
                );
              }}
              icon='true'
            >
              <IconStyled
                name={conf[accountType].icon}
                notSet={!accountTypeMap[accountType]}
              />
            </Item>
          ))}
          <HoverElement
            condition={!!accountEditor}
            offSetX={-10}
            offSetY={32}
            onHideInput={socialAccounts.StopEditAccount}
            element={this.element}
          >
            <Input
              type='text'
              value={
                accountEditor &&
                `${conf[accountEditor.accountType].prefix}${
                  accountEditor.accountName
                }`
              }
              onChange={e => accountEditor.UpdateValue(e.target.value)}
              onBlur={this.onBlur}
              onFocus={e => {
                e.target.setSelectionRange(
                  e.target.value.length,
                  e.target.value.length,
                );
              }}
              onClick={e => {
                e.stopPropagation();
              }}
              ref={ref => {
                this._input = ref;
              }}
              width='320px'
              onKeyDown={this.onEnter}
            />
          </HoverElement>
        </List>
        <List
          condition={!editing}
          parent={app.constructor.registryName}
          noIcons={accountsNumber === 0}
          isProfile={isProfile}
        >
          <Item>
            <Count
              onClick={
                followersCount === 0 ? () => {} : () => app.ShowFollowers()
              }
            >
              {followersCount}
            </Count>{' '}
            <Trans>Followers</Trans>
          </Item>
          <ConditionalItem condition={isProfile}>
            <Count
              onClick={
                followingCount === 0 ? () => {} : () => app.ShowFollowing()
              }
            >
              {followingCount}
            </Count>{' '}
            <Trans>Following</Trans>
          </ConditionalItem>
          <ConditionalItem condition={accountsNumber > 0} divider>
            —
          </ConditionalItem>
          {// FIXME - This needs checking
          // eslint-disable-next-line
          Object.keys(conf).map(accountType => {
            const account = accountTypeMap[accountType];
            if (account) {
              let href;
              if (['WEBSITE', 'FABLABSIO'].includes(account.accountType)) {
                if (account.accountName.includes('://')) {
                  href = `${account.accountName}`;
                } else {
                  href = `//${account.accountName}`;
                }
              } else {
                href = `${conf[account.accountType].prefix}${
                  account.accountName
                }`;
              }
              return (
                <a
                  key={account.accountType}
                  href={href}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  <Item icon='true'>
                    <IconStyled name={conf[account.accountType].icon} />
                  </Item>
                </a>
              );
            }
          })}
        </List>
      </Wrapper>
    );
  }
}

const List = withConditional(styled.ul`
  margin: ${props => (props.isProfile ? '0 0 20px 0' : '10px 0 10px 0')};
  height: 21px;
  color: ${props => props.theme.color.wfGrey900};
  font-size: ${props => (props.isProfile ? '1.0625em' : '0.9em')};
  > li:first-child {
    margin-left: 0;
  }
  ${media.s`
    height: ${props => (props.noIcons ? '21px' : '55px')};
    > li:nth-of-type(${props => (props.parent === 'ProfileApp' ? '3' : '2')}) {
      margin-left: 0;
    }
    > a:first-of-type > li {
      margin-left: 0;
    }
  `}
`);

const Item = styled.li`
  display: inline-block;
  margin-left: ${props =>
    props.icon ? '12px' : props.noMarginRight ? '0px' : '16px'};
  ${media.s`
    display: ${props => (props.divider ? 'block' : 'inline-block')};
    margin-left: ${props =>
      props.divider
        ? 'none'
        : props.icon
        ? '12px'
        : props.noMarginRight
        ? '0px'
        : '16px'};
  `}
`;

const ConditionalItem = withConditional(Item);

const Count = styled.strong`
  color: ${props => props.theme.color.primary};
  cursor: pointer;
  &:hover {
    color: ${props => props.theme.color.primary};
    text-decoration: underline;
    ${media.s`
      text-decoration: none;
    `}
  }
`;

const IconStyled = styled(Icon)`
  cursor: ${props => (props.notSet ? 'copy' : 'pointer')};
  color: ${props =>
    props.notSet ? props.theme.color.wfGrey200 : props.theme.color.wfGrey900};
  &:hover {
    color: ${props =>
      props.notSet ? props.theme.color.wfGrey500 : props.theme.color.primary};
  }
`;

SocialStats.propTypes = {
  app: PropTypes.object,
};
